import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './PowerBiReport.css';
import baseApi from '../../common/api/base';
import * as pbi from 'powerbi-client'
import { PowerBIEmbed } from 'powerbi-client-react';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';


export default function PowerBiReport({ report: propsReport }) {
  const environmentContext = useContext(EnvironmentContext);
  const { hasToken } = environmentContext;
  const { reportId } = useParams();
  const powerBIEmbeddedReport = useRef(null);

  const [config, setConfig] = useState({
    type: 'report',
    id: undefined,
    embedUrl: undefined,
    accessToken: undefined,
    tokenType: pbi.models.TokenType.Embed,
    permissions: pbi.models.Permissions.All,
    settings: {
      customLayout: { displayOption: pbi.models.DisplayOption.FitToPage },
      panes: {
        filters: {
          expanded: false,
          visible: false
        }
      },
      background: pbi.models.BackgroundType.Transparent,
      filterPaneEnabled: false,
      navContentPaneEnabled: true

    }
  });


  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');


  const monitoredIndividualFieldRef = useRef(null);
  const monitoredIndividualTableRef = useRef(null);
  const unitFieldRef = useRef(null);
  const unitTableRef = useRef(null);
  const dateFieldRef = useRef(null);
  const dateTableRef = useRef(null);
  const dateSlicerRef = useRef(null);


  const setMonitoredIndividualFieldRef = (el) => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport setMonitoredIndividualFieldRef'); }
    monitoredIndividualFieldRef.current = el;
  }


  const setMonitoredIndividualTableRef = (el) => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport setMonitoredIndividualTableRef'); }
    monitoredIndividualTableRef.current = el;
  }


  const setUnitFieldRef = (el) => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport setUnitFieldRef'); }
    unitFieldRef.current = el;

  }

  const setUnitTableRef = (el) => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport setUnitTableRef'); }
    unitTableRef.current = el;
  }


  const setDateFieldRef = (el) => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport setDataFieldRef'); }
    dateFieldRef.current = el;

  }

  const setDateTableRef = (el) => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport setDataTableRef'); }
    dateTableRef.current = el;
  }

  const setDateSlicerRef = (el) => {  
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport setDateSlicerRef'); }
    dateSlicerRef.current = el;
  }

  if (logComponent('PowerBiReport')) { console.debug('PowerBiReport rendered'); }


  useEffect(() => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport onMount'); }
    window.scrollTo(0, 0);

    if (!hasToken) return;

    loadReportToken();
  }, [])

  useEffect(() => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport useEffect [environmentContext.hasToken, reportId]'); }
    if (!hasToken) return;

    loadReportToken();
  }, [environmentContext.hasToken, reportId])

  useEffect(() => {
    const setReportDefaultSize = async (pbiReport) => {
      if (pbiReport) {
        return new Promise((resolve, reject) => {
          // Listen for 'loaded' event
          pbiReport.on('loaded', async () => {
            try {
              console.log("Checking for report orientation.")
              const pages = await pbiReport.getPages();
              if (pages.length > 0) {

                const page = pages[0];
                const size = page.defaultSize;
                let embeddedContainerElement = document.getElementsByClassName('embed-container');
                if (embeddedContainerElement && embeddedContainerElement[0] && size.height > size.width) {
                  embeddedContainerElement[0].classList.remove('landscape');
                  embeddedContainerElement[0].classList.add('portrait');
                  console.log("Changed report orientation to portrait.")
                }

                page.getVisuals().then(visuals => {
                  let visual = visuals.filter(function (visual) { return visual.type === 'slicer' && visual.title === dateSlicerRef.current })[0];

                  const state = visual.getSlicerState();
                  console.log(state);
                  state.then(slicer => {
                    console.log("slicer state:  " + JSON.stringify(slicer));
                    if (slicer.filters.length > 0) {
                      let slicerFilter = slicer.filters[0];
                      console.log("slicer state filter:  " + JSON.stringify(slicerFilter))

                      const newRelativeDateFilter = {
                        $schema: slicerFilter.$schema,
                        target: slicerFilter.target,
                        operator: slicerFilter.operator,
                        timeUnitsCount: slicerFilter.timeUnitsCount + 1,
                        timeUnitType: slicerFilter.timeUnitType,
                        includeToday: slicerFilter.includeToday,
                        filterType: slicerFilter.filterType
                      };
                      visual.setSlicerState({ filters: [newRelativeDateFilter] }).then(newState => { console.log("Date slicer was set."); });
                      newRelativeDateFilter.timeUnitsCount = newRelativeDateFilter.timeUnitsCount - 1;
                      visual.setSlicerState({ filters: [newRelativeDateFilter] }).then(newState => {
                        console.log("Date slicer was unset.");
                      });
                    } else {
                      const newRelativeDateFilter = {
                        $schema: "http://powerbi.com/product/schema#relativeDate",
                        target: {
                          table: dateTableRef.current,
                          column: dateFieldRef.current
                        },
                        operator: 1, // In This
                        timeUnitsCount: 1,
                        timeUnitType: 3,  //Months
                        includeToday: true,
                        filterType: 4  //Relative Date
                      };
                      visual.setSlicerState({ filters: [newRelativeDateFilter] }).then(newState => { console.log("Date slicer was set."); });
                    }


                  });
                });
              }
              resolve();
            } catch (error) {
              reject(error);
            }
          });
        });
      }
    }
    setReportDefaultSize(powerBIEmbeddedReport.current).catch(error => console.error(error));
  }, [powerBIEmbeddedReport.current])

  const setFilters = (filterUnitId, filterMonitoredIndividualId) => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport setFilters'); }
    if (!filterUnitId && !filterMonitoredIndividualId) {
      delete config.filters;
    }
    if (filterUnitId) {
      let filters = [{
        $schema: "http://powerbi.com/product/schema#advanced",
        target: {
          table: unitTableRef.current,
          column: unitFieldRef.current
        },
        operator: "In",
        values: [filterUnitId]
      }];

      config.filters = filters;
    }

    if (filterMonitoredIndividualId) {
      let filters = [{
        $schema: "http://powerbi.com/product/schema#advanced",
        target: {
          table: monitoredIndividualTableRef.current,
          column: monitoredIndividualFieldRef.current
        },
        operator: "In",
        values: [filterMonitoredIndividualId]
      }];

      config.filters = filters;
    }
  }


  const loadReportToken = () => {
    if (logComponent('PowerBiReport')) { console.debug('PowerBiReport loadReportToken'); }
    if (!hasToken) return;

    setReportsLoading(true);
    setReportsError(false);




    baseApi.Reports.getReportToken(reportId)
      .then(
        results => {
          let reportConfig = { ...config }
          reportConfig.accessToken = results.data.token;
          reportConfig.id = results.data.reportId;
          reportConfig.embedUrl = results.data.url;
          //setSlicers(reportConfig);

          if (window.matchMedia('(max-width: 600px)').matches) reportConfig.settings = { layoutType: pbi.models.LayoutType.MobilePortrait };
          setConfig(reportConfig);
          setReportsLoading(false);
          setReportsError(false);
          setMonitoredIndividualFieldRef(results.data.monitoredIndividualField);
          setMonitoredIndividualTableRef(results.data.monitoredIndividualTable);
          setUnitFieldRef(results.data.unitField);
          setUnitTableRef(results.data.unitTable);
          setDateFieldRef(results.data.dateField);
          setDateTableRef(results.data.dateTable);
          setDateSlicerRef(results.data.dateSlicer);

          setFilters(environmentContext.selectedUnit?.unitId, environmentContext.selectedMonitoredIndividual?.monitoredIndividualId);

        },
        error => {
          setReportsLoading(false);
          setReportsError(true);
          setMonitoredIndividualFieldRef(null);
          setMonitoredIndividualTableRef(null);
          setUnitFieldRef(null);
          setUnitTableRef(null);
          setErrorMessage('Error Loading Report');
        }
      );
  }


  return (
    <div className="Power-Bi-Report">
      {
        reportsError == true && <span>{errorMessage}</span>
      }

      {
        !reportsError && reportsLoading && <span>Loading</span>
      }
      {
        !reportsError && !reportsLoading &&
        <PowerBIEmbed
          embedConfig={config}

          eventHandlers={

            new Map([
              ['loaded', function () { console.log('Report loaded'); }],
              ['rendered', function () { console.log('Report rendered'); }],
              ['error', function (event) { console.log(event.detail); }]
            ])

          }

          cssClassName={"embed-container landscape"}

          getEmbeddedComponent={(embeddedReport) => {
            powerBIEmbeddedReport.current = embeddedReport;
            window.report = embeddedReport;
          }}
        />
      }
    </div>
  );
}